import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Camera, Check, ChevronDown, ChevronLeft, ChevronRight, ClipboardList, Download, Eye, IndianRupee, MessageSquare,
  Pencil, Phone, Plus, RotateCcw, Search, Trash2, User, UserRoundCheck, X,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

const inputBase = 'w-full border rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const inputCls = 'w-full border border-gray-300 rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const inputErr = `${inputBase} border-red-400`;
const maxDocumentFileSize = 25 * 1024 * 1024;

const emptyForm = {
  caseName: '',
  mobileNumber: '',
  referredBy: '',
  loanType: '',
  amountRequired: '',
  remarks: '',
  callStatus: '',
  callBackDate: '',
  callBackTime: '',
  dispositionChecked: false,
  dispositionRemark: '',
};

const callStatusOptions = [
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'warm_lead',      label: 'Interested' },
  { value: 'call_back',      label: 'Call Back' },
  { value: 'cold_lead',      label: 'Follow Up' },
  { value: 'not_connected',  label: 'Not Connected' },
];

const callStatusLabel = val =>
  callStatusOptions.find(o => o.value === val)?.label || val || '—';

const phoneOk = v => /^[0-9]{10}$/.test(String(v || '').trim());
const amountOk = v => v === '' || (!Number.isNaN(Number(v)) && Number(v) >= 0);

// Converts "DD/MM/YYYY" → "YYYY-MM-DD"; passes through other formats unchanged
const parseDateForBackend = str => {
  if (!str) return str;
  const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${String(m[2]).padStart(2,'0')}-${String(m[1]).padStart(2,'0')}`;
  return str;
};

const rules = {
  caseName:       v => v.trim() ? '' : 'Case name is required.',
  mobileNumber:   v => phoneOk(v) ? '' : 'Mobile number must be 10 digits.',
  amountRequired: v => amountOk(v) ? '' : 'Amount must be a positive number.',
};

const fieldIcons = {
  caseName:       User,
  mobileNumber:   Phone,
  referredBy:     UserRoundCheck,
  loanType:       ClipboardList,
  amountRequired: IndianRupee,
  remarks:        MessageSquare,
};

const FieldIcon = ({ field, multiline = false }) => {
  const Icon = fieldIcons[field];
  if (!Icon) return null;
  return (
    <Icon
      size={18} strokeWidth={2}
      className={`pointer-events-none absolute right-3 ${multiline ? 'top-3' : 'top-1/2 -translate-y-1/2'} text-gray-400`}
      aria-hidden="true"
    />
  );
};

// ── Multi-Select Filter ────────────────────────────────────────────────────────
function MultiSelect({ options, value = [], onChange, placeholder = 'All' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = v => {
    if (value.includes(v)) onChange(value.filter(x => x !== v));
    else onChange([...value, v]);
  };

  const allSelected = options.length > 0 && value.length === options.length;
  const someSelected = value.length > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) onChange([]);
    else onChange(options.map(o => o.value));
  };

  const label = value.length === 0 || allSelected
    ? placeholder
    : value.length === 1
      ? (options.find(o => o.value === value[0])?.label || value[0])
      : `${value.length} selected`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-gray-400 transition-colors"
      >
        <span className={value.length && !allSelected ? 'text-gray-800 font-medium truncate' : 'text-gray-400'}>{label}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value.length > 0 && (
            <span
              role="button"
              onClick={e => { e.stopPropagation(); onChange([]); }}
              className="text-gray-300 hover:text-gray-500 cursor-pointer"
            >
              <X size={12} />
            </span>
          )}
          <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-52 overflow-y-auto">
          {/* Select All row */}
          <label className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors border-b border-gray-100 bg-gray-50">
            <input
              type="checkbox"
              checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected; }}
              onChange={toggleAll}
              className="w-4 h-4 rounded border-gray-300 accent-blue-600 cursor-pointer flex-shrink-0"
            />
            <span className="text-sm font-semibold text-gray-700">Select All</span>
          </label>
          {options.map(o => (
            <label key={o.value} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors">
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="w-4 h-4 rounded border-gray-300 accent-blue-600 cursor-pointer flex-shrink-0"
              />
              <span className="text-sm text-gray-700">{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const Field = ({ field, label, touched, errs, focusedField, children }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    {children}
    {touched[field] && focusedField !== field && errs[field] && (
      <p className="text-xs text-red-600 mt-1">{errs[field]}</p>
    )}
  </div>
);

const TextInput = ({ field, label, type = 'text', placeholder, form, touched, errs, focusedField, onChange, onBlur, onFocus }) => (
  <Field field={field} label={label} touched={touched} errs={errs} focusedField={focusedField}>
    <div className="relative">
      <input
        type={type}
        className={touched[field] && focusedField !== field && errs[field] ? inputErr : inputCls}
        placeholder={placeholder || label}
        value={form[field]}
        onChange={e => onChange(field, e.target.value)}
        onFocus={() => onFocus(field)}
        onBlur={e => onBlur(field, e.target.value)}
        inputMode={field === 'mobileNumber' ? 'numeric' : undefined}
      />
      <FieldIcon field={field} />
    </div>
  </Field>
);

const AmountInput = props => <TextInput {...props} type="number" placeholder="0" />;

// ── Camera Modal ──────────────────────────────────────────────────────────────
const CameraModal = ({ field, label, onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState('');
  const [capturedFile, setCapturedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    let stream;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err) {
        setError(err?.message || 'Camera is not available on this device.');
      }
    })();
    return () => { stream?.getTracks().forEach(t => t.stop()); };
  }, []);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const capture = () => {
    const video = videoRef.current; const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], `${field}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setCapturedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }, 'image/jpeg', 0.9);
  };

  const retake = () => { if (previewUrl) URL.revokeObjectURL(previewUrl); setCapturedFile(null); setPreviewUrl(''); };
  const usePhoto = () => { if (!capturedFile) return; onCapture(field, capturedFile); onClose(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">Capture {label || field}</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl leading-none">&times;</button>
        </div>
        <div className="bg-black">
          {error ? <div className="p-6 text-sm text-white text-center">{error}</div>
            : previewUrl ? <img src={previewUrl} alt="preview" className="w-full aspect-[3/4] object-cover" />
            : <video ref={videoRef} autoPlay playsInline muted className="w-full aspect-[3/4] object-cover" />}
          <canvas ref={canvasRef} className="hidden" />
        </div>
        <div className="grid grid-cols-2 gap-2 p-4 sm:flex sm:justify-end">
          <button type="button" onClick={previewUrl ? retake : onClose} className="inline-flex items-center justify-center border border-gray-300 hover:bg-gray-50 text-gray-700 w-11 h-11 rounded-lg transition-colors">
            {previewUrl ? <RotateCcw size={16} /> : <X size={16} />}
          </button>
          {!previewUrl && (
            <button type="button" onClick={capture} disabled={!!error} className="inline-flex items-center justify-center border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:border-gray-300 disabled:text-gray-400 w-11 h-11 rounded-lg transition-colors">
              <Camera size={16} />
            </button>
          )}
          {previewUrl && <>
            <button type="button" onClick={onClose} className="inline-flex items-center justify-center border border-gray-300 hover:bg-gray-50 text-gray-700 w-11 h-11 rounded-lg transition-colors"><X size={16} /></button>
            <button type="button" onClick={usePhoto} className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white w-11 h-11 rounded-lg transition-colors"><Check size={16} /></button>
          </>}
        </div>
      </div>
    </div>
  );
};

// ── Document Capture Card ─────────────────────────────────────────────────────
const DocumentCapture = ({ field, label, files = [], error, onChange, onClear, onError }) => (
  <div className={`border rounded-lg bg-gray-50 p-2 ${error ? 'border-red-300' : 'border-gray-200'}`}>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label || field}</label>
    <div className="flex flex-wrap items-center gap-1">
      <label className="inline-flex items-center justify-center border border-gray-300 hover:bg-white text-gray-700 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer">
        Choose Files
        <input type="file" multiple accept="image/*,application/pdf,.xlsx,.xls,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={e => {
          e.preventDefault(); e.stopPropagation();
          const selected = Array.from(e.target.files || []);
          const oversized = selected.find(f => f.size > maxDocumentFileSize);
          if (oversized) { e.target.value = ''; onError?.(`${label || field}: each file must be 25 MB or less.`); return; }
          if (selected.length) onChange(field, selected);
          e.target.value = '';
        }} />
      </label>
      {files.length > 0 && (
        <button type="button" onClick={() => onClear(field, null)} className="border border-red-200 hover:bg-red-50 text-red-600 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors">
          Remove All
        </button>
      )}
    </div>
    {files.length === 0 ? (
      <p className="text-xs text-gray-400 mt-1">No files selected</p>
    ) : (
      <ul className="mt-1 space-y-0.5">
        {files.map((f, idx) => (
          <li key={idx} className="flex items-center justify-between gap-1">
            <span className="text-xs text-gray-600 truncate">{f.name}</span>
            <button type="button" onClick={() => onClear(field, idx)} className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors">
              <X size={11} />
            </button>
          </li>
        ))}
      </ul>
    )}
    {error && <p className="text-xs text-red-600 mt-0.5">{error}</p>}
  </div>
);

// ── Add Caller Modal ──────────────────────────────────────────────────────────
function AddCallerModal({ open, onClose, onSaved, userInfo, loanTypes, showToast }) {
  const [form, setForm] = useState(emptyForm);
  const [formKey, setFormKey] = useState(0);
  const [errs, setErrs] = useState({});
  const [documentErrs, setDocumentErrs] = useState({});
  const [touched, setTouched] = useState({});
  const [reqDocs, setReqDocs] = useState({});
  const [cameraField, setCameraField] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const autoSaveTimer = useRef(null);
  const latestFormRef = useRef(form);
  const latestDraftIdRef = useRef(null);

  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };
  const draftLocalKey = `callerDraft_${userInfo?._id || 'anon'}`;

  useEffect(() => { latestFormRef.current = form; }, [form]);
  useEffect(() => { latestDraftIdRef.current = draftId; }, [draftId]);

  useEffect(() => {
    if (open) {
      const saved = localStorage.getItem(draftLocalKey);
      if (saved) {
        try {
          const { draftId: sid, form: sf } = JSON.parse(saved);
          const restored = { ...emptyForm, referredBy: userInfo?.name || '', ...sf };
          setForm(restored);
          latestFormRef.current = restored;
          if (sid) { setDraftId(sid); latestDraftIdRef.current = sid; }
          else { setDraftId(null); latestDraftIdRef.current = null; }
        } catch {
          const freshForm = { ...emptyForm, referredBy: userInfo?.name || '' };
          setForm(freshForm); latestFormRef.current = freshForm;
          setDraftId(null); latestDraftIdRef.current = null;
        }
      } else {
        const freshForm = { ...emptyForm, referredBy: userInfo?.name || '' };
        setForm(freshForm); latestFormRef.current = freshForm;
        setDraftId(null); latestDraftIdRef.current = null;
      }
      setErrs({}); setTouched({}); setReqDocs({}); setDocumentErrs({});
      setFormKey(k => k + 1); setLastSaved(null); setAutoSaving(false);
    } else {
      if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
    }
  }, [open]); // eslint-disable-line

  const performAutoSave = async () => {
    setAutoSaving(true);
    try {
      const body = new FormData();
      body.append('isDraft', 'true');
      Object.entries(latestFormRef.current).forEach(([k, v]) => {
        if (k === 'callBackTime') return;
        if (k === 'callBackDate' && latestFormRef.current.callBackDate && latestFormRef.current.callBackTime) {
          body.append(k, `${parseDateForBackend(v)}T${latestFormRef.current.callBackTime}:00`);
        } else { body.append(k, v); }
      });
      let id = latestDraftIdRef.current;
      if (id) {
        await axios.put(`${API}/callers/${id}`, body, authConfig);
      } else {
        const { data } = await axios.post(`${API}/callers`, body, authConfig);
        id = data.caller._id;
        setDraftId(id);
        latestDraftIdRef.current = id;
      }
      localStorage.setItem(draftLocalKey, JSON.stringify({ draftId: id, form: latestFormRef.current }));
      setLastSaved(new Date());
    } catch {} finally { setAutoSaving(false); }
  };

  const onChange = (field, value) => {
    const v = field === 'mobileNumber' ? value.replace(/\D/g, '').slice(0, 10) : value;
    const nextForm = { ...form, [field]: v };
    setForm(nextForm);
    latestFormRef.current = nextForm;
    if (field === 'loanType') { setReqDocs({}); setDocumentErrs({}); }
    localStorage.setItem(draftLocalKey, JSON.stringify({ draftId: latestDraftIdRef.current, form: nextForm }));
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      if (latestFormRef.current.caseName?.trim()) performAutoSave();
    }, 8000);
  };

  const onBlur = (field, value = form[field]) => {
    setFocusedField('');
    const v = field === 'mobileNumber' ? value.replace(/\D/g, '').slice(0, 10) : value;
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrs(prev => ({ ...prev, [field]: rules[field] ? rules[field](v) : '' }));
  };

  const onDocChange = (docLabel, newFiles) => {
    setReqDocs(prev => ({ ...prev, [docLabel]: [...(prev[docLabel] || []), ...newFiles] }));
    setDocumentErrs(prev => ({ ...prev, [docLabel]: '' }));
  };

  const clearDoc = (docLabel, fileIdx) => {
    if (fileIdx === null) {
      setReqDocs(prev => ({ ...prev, [docLabel]: [] }));
    } else {
      setReqDocs(prev => ({ ...prev, [docLabel]: (prev[docLabel] || []).filter((_, i) => i !== fileIdx) }));
    }
    setDocumentErrs(prev => ({ ...prev, [docLabel]: '' }));
  };

  const submit = async e => {
    e.preventDefault();
    if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
    const next = {};
    Object.keys(rules).forEach(k => { const m = rules[k](form[k]); if (m) next[k] = m; });
    setErrs(next); setTouched(Object.fromEntries(Object.keys(rules).map(k => [k, true])));
    if (Object.keys(next).length) return;

    const selectedLT = loanTypes.find(lt => lt.name === form.loanType);
    const requiredDocs = selectedLT?.requiredDocuments || [];

    const body = new FormData();
    body.append('isDraft', 'false');
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'callBackTime') return;
      if (k === 'callBackDate' && form.callBackDate && form.callBackTime) {
        body.append(k, `${parseDateForBackend(v)}T${form.callBackTime}:00`);
      } else { body.append(k, v); }
    });
    requiredDocs.forEach((docName, i) => {
      body.append(`reqDocName_${i}`, docName);
      (reqDocs[docName] || []).forEach((file, j) => body.append(`reqDoc_${i}_${j}`, file));
    });

    setSubmitting(true);
    try {
      let merged = false;
      if (draftId) {
        await axios.put(`${API}/callers/${draftId}`, body, authConfig);
      } else {
        const { data } = await axios.post(`${API}/callers`, body, authConfig);
        merged = !!data?.merged;
      }
      localStorage.removeItem(draftLocalKey);
      showToast('success', merged
        ? 'Matching case found — status change logged to its history.'
        : 'Caller enquiry submitted.');
      onSaved();
      onClose();
    } catch (err) {
      showToast('error', err?.response?.data?.message || err?.message || 'Failed to submit.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const inputProps = { form, touched, errs, focusedField, onChange, onBlur, onFocus: setFocusedField };
  const selectedLT = loanTypes.find(lt => lt.name === form.loanType);
  const reqList = selectedLT?.requiredDocuments || [];

  return (
    <>
      {cameraField && (
        <CameraModal field={cameraField} label={cameraField} onCapture={onDocChange} onClose={() => setCameraField('')} />
      )}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-white/10 backdrop-blur-sm p-4"
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="bg-white rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.18),0_8px_32px_rgba(0,0,0,0.12)] ring-1 ring-black/5 w-full max-w-5xl flex flex-col max-h-[95vh]">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white rounded-t-2xl flex-shrink-0">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-800">Add Caller Enquiry</h2>
                <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full border border-blue-100">
                  <User size={11} />
                  {userInfo?.name || '—'}
                </span>
              </div>
              {autoSaving && <p className="text-xs text-blue-500 mt-0.5">Saving draft…</p>}
              {!autoSaving && lastSaved && <p className="text-xs text-green-600 mt-0.5">Draft auto-saved</p>}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={18} /></button>
          </div>

          <div className="px-4 py-3 overflow-y-auto flex-1">
            <form key={formKey} onSubmit={submit} className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <TextInput field="caseName" label="Case Name *" {...inputProps} />
                <TextInput field="mobileNumber" label="Mobile Number *" placeholder="10-digit mobile number" {...inputProps} />
                <TextInput field="referredBy" label="Ref. by" {...inputProps} />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type</label>
                  <select value={form.loanType} onChange={e => onChange('loanType', e.target.value)} className={inputCls}>
                    <option value="">Select loan type</option>
                    {loanTypes.map(lt => <option key={lt._id} value={lt.name}>{lt.name}</option>)}
                  </select>
                </div>
                <AmountInput field="amountRequired" label="Amount Required" {...inputProps} />
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                  <select value={form.callStatus} onChange={e => onChange('callStatus', e.target.value)} className={inputCls}>
                    <option value="">Select status</option>
                    {callStatusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
              </div>

              <Field field="remarks" label="Remarks" touched={touched} errs={errs} focusedField={focusedField}>
                <div className="relative">
                  <textarea rows={3} className={inputCls} placeholder="Remarks" value={form.remarks}
                    onChange={e => onChange('remarks', e.target.value)}
                    onFocus={() => setFocusedField('remarks')}
                    onBlur={e => onBlur('remarks', e.target.value)}
                  />
                  <FieldIcon field="remarks" multiline />
                </div>
              </Field>

              {form.callStatus === 'call_back' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Call Back Date *</label>
                    <input
                      type="text"
                      className={inputCls}
                      value={form.callBackDate}
                      placeholder="DD/MM/YYYY"
                      onChange={e => onChange('callBackDate', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Call Back Time</label>
                    <input
                      type="text"
                      className={inputCls}
                      value={form.callBackTime}
                      placeholder="HH:MM"
                      onChange={e => onChange('callBackTime', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {form.callStatus === 'cold_lead' && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-700">
                    Call back will be automatically scheduled 15 days from today ({new Date(Date.now() + 15 * 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}).
                  </p>
                </div>
              )}

              <div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={!!form.dispositionChecked}
                    onChange={e => onChange('dispositionChecked', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">Disposition</span>
                </label>
                {form.dispositionChecked && (
                  <textarea rows={2} className={`${inputCls} mt-2`} placeholder="Disposition remark"
                    value={form.dispositionRemark} onChange={e => onChange('dispositionRemark', e.target.value)}
                  />
                )}
              </div>

              <div>
                <h2 className="text-sm font-semibold text-gray-700 mb-3">Document Images</h2>
                {!form.loanType ? (
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 py-10 text-center">
                    <svg className="w-10 h-10 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m0 0v2m0-2h2m-2 0H10m2-6a4 4 0 100-8 4 4 0 000 8zm-9 9a9 9 0 1118 0H3z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-400">Select a loan type to upload documents</p>
                  </div>
                ) : reqList.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No required documents defined for this loan type.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {reqList.map(docName => (
                      <DocumentCapture key={docName} field={docName} label={docName}
                        files={reqDocs[docName] || []} error={documentErrs[docName]}
                        onChange={onDocChange} onClear={clearDoc} onCamera={setCameraField}
                        onError={msg => showToast('error', msg)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-6 py-2 rounded-lg transition-colors">
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Edit Caller Modal ─────────────────────────────────────────────────────────
function EditCallerModal({ open, onClose, onSaved, userInfo, loanTypes, showToast, initial }) {
  const [form, setForm] = useState(emptyForm);
  const [formKey, setFormKey] = useState(0);
  const [errs, setErrs] = useState({});
  const [touched, setTouched] = useState({});
  const [reqDocs, setReqDocs] = useState({});
  const [documentErrs, setDocumentErrs] = useState({});
  const [cameraField, setCameraField] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  useEffect(() => {
    if (open && initial) {
      setForm({
        caseName:           initial.caseName || '',
        mobileNumber:       initial.mobileNumber || '',
        referredBy:         initial.referredBy || '',
        loanType:           initial.loanType || '',
        amountRequired:     initial.amountRequired != null ? String(initial.amountRequired) : '',
        remarks:            initial.remarks || '',
        callStatus:         initial.callStatus || '',
        callBackDate:       initial.callBackDate ? (() => { const d = new Date(initial.callBackDate); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })() : '',
        callBackTime:       initial.callBackDate ? (() => { const d = new Date(initial.callBackDate); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })() : '',
        dispositionChecked: !!initial.dispositionChecked,
        dispositionRemark:  initial.dispositionRemark || '',
      });
      setErrs({}); setTouched({}); setReqDocs({}); setDocumentErrs({});
      setFormKey(k => k + 1);
    }
  }, [open, initial]);

  const onChange = (field, value) => {
    const v = field === 'mobileNumber' ? value.replace(/\D/g, '').slice(0, 10) : value;
    setForm(prev => ({ ...prev, [field]: v }));
    if (field === 'loanType') { setReqDocs({}); setDocumentErrs({}); }
  };

  const onBlur = (field, value = form[field]) => {
    setFocusedField('');
    const v = field === 'mobileNumber' ? value.replace(/\D/g, '').slice(0, 10) : value;
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrs(prev => ({ ...prev, [field]: rules[field] ? rules[field](v) : '' }));
  };

  const onDocChange = (docLabel, newFiles) => {
    setReqDocs(prev => ({ ...prev, [docLabel]: [...(prev[docLabel] || []), ...newFiles] }));
    setDocumentErrs(prev => ({ ...prev, [docLabel]: '' }));
  };

  const clearDoc = (docLabel, fileIdx) => {
    if (fileIdx === null) {
      setReqDocs(prev => ({ ...prev, [docLabel]: [] }));
    } else {
      setReqDocs(prev => ({ ...prev, [docLabel]: (prev[docLabel] || []).filter((_, i) => i !== fileIdx) }));
    }
    setDocumentErrs(prev => ({ ...prev, [docLabel]: '' }));
  };

  const submit = async e => {
    e.preventDefault();
    const next = {};
    Object.keys(rules).forEach(k => { const m = rules[k](form[k]); if (m) next[k] = m; });
    setErrs(next); setTouched(Object.fromEntries(Object.keys(rules).map(k => [k, true])));
    if (Object.keys(next).length) return;

    const selectedLT = loanTypes.find(lt => lt.name === form.loanType);
    const requiredDocs = selectedLT?.requiredDocuments || [];
    const body = new FormData();
    body.append('isDraft', 'false');
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'callBackTime') return;
      if (k === 'callBackDate' && form.callBackDate && form.callBackTime) {
        body.append(k, `${parseDateForBackend(v)}T${form.callBackTime}:00`);
      } else { body.append(k, v); }
    });
    requiredDocs.forEach((docName, i) => {
      body.append(`reqDocName_${i}`, docName);
      (reqDocs[docName] || []).forEach((file, j) => body.append(`reqDoc_${i}_${j}`, file));
    });

    setSubmitting(true);
    try {
      await axios.put(`${API}/callers/${initial._id}`, body, authConfig);
      showToast('success', 'Caller enquiry updated.');
      onSaved();
      onClose();
    } catch (err) {
      showToast('error', err?.response?.data?.message || err?.message || 'Failed to update.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const inputProps = { form, touched, errs, focusedField, onChange, onBlur, onFocus: setFocusedField };
  const selectedLT = loanTypes.find(lt => lt.name === form.loanType);
  const reqList = selectedLT?.requiredDocuments || [];
  const existingDocs = initial?.reqDocuments || [];

  return (
    <>
      {cameraField && (
        <CameraModal field={cameraField} label={cameraField} onCapture={onDocChange} onClose={() => setCameraField('')} />
      )}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-white/10 backdrop-blur-sm p-4"
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="bg-white rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.18),0_8px_32px_rgba(0,0,0,0.12)] ring-1 ring-black/5 w-full max-w-5xl flex flex-col max-h-[95vh]">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white rounded-t-2xl flex-shrink-0">
            <h2 className="text-base font-semibold text-gray-800">Edit Caller Enquiry</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={18} /></button>
          </div>

          <div className="px-4 py-3 overflow-y-auto flex-1">
            <div className="mb-3 pb-3 border-b border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1">Caller</label>
              <div className={`${inputCls} max-w-sm bg-gray-50 text-gray-700 cursor-default`}>
                {initial?.callerName || initial?.caller?.name || userInfo?.name || '—'}
              </div>
            </div>

            <form key={formKey} onSubmit={submit} className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <TextInput field="caseName" label="Case Name *" {...inputProps} />
                <TextInput field="mobileNumber" label="Mobile Number *" placeholder="10-digit mobile number" {...inputProps} />
                <TextInput field="referredBy" label="Ref. by" {...inputProps} />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type</label>
                  <select value={form.loanType} onChange={e => onChange('loanType', e.target.value)} className={inputCls}>
                    <option value="">Select loan type</option>
                    {loanTypes.map(lt => <option key={lt._id} value={lt.name}>{lt.name}</option>)}
                  </select>
                </div>
                <AmountInput field="amountRequired" label="Amount Required" {...inputProps} />
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                  <select value={form.callStatus} onChange={e => onChange('callStatus', e.target.value)} className={inputCls}>
                    <option value="">Select status</option>
                    {callStatusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
              </div>

              <Field field="remarks" label="Remarks" touched={touched} errs={errs} focusedField={focusedField}>
                <div className="relative">
                  <textarea rows={3} className={inputCls} placeholder="Remarks" value={form.remarks}
                    onChange={e => onChange('remarks', e.target.value)}
                    onFocus={() => setFocusedField('remarks')}
                    onBlur={e => onBlur('remarks', e.target.value)}
                  />
                  <FieldIcon field="remarks" multiline />
                </div>
              </Field>

              {form.callStatus === 'call_back' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Call Back Date *</label>
                    <input
                      type="text"
                      className={inputCls}
                      value={form.callBackDate}
                      placeholder="DD/MM/YYYY"
                      onChange={e => onChange('callBackDate', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Call Back Time</label>
                    <input
                      type="text"
                      className={inputCls}
                      value={form.callBackTime}
                      placeholder="HH:MM"
                      onChange={e => onChange('callBackTime', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {form.callStatus === 'cold_lead' && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-700">
                    Call back will be automatically scheduled 15 days from today ({new Date(Date.now() + 15 * 86400000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}).
                  </p>
                </div>
              )}

              <div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={!!form.dispositionChecked}
                    onChange={e => onChange('dispositionChecked', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">Disposition</span>
                </label>
                {form.dispositionChecked && (
                  <textarea rows={2} className={`${inputCls} mt-2`} placeholder="Disposition remark"
                    value={form.dispositionRemark} onChange={e => onChange('dispositionRemark', e.target.value)}
                  />
                )}
              </div>

              <div>
                <h2 className="text-sm font-semibold text-gray-700 mb-3">Document Images</h2>
                {existingDocs.length > 0 && (
                  <div className="mb-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Previously uploaded</p>
                    <ul className="space-y-1">
                      {existingDocs.map((d, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${d.fileUrl ? 'bg-green-500' : 'bg-gray-300'}`} />
                          {d.name}
                          {d.fileUrl
                            ? <span className="text-green-600 font-medium">— uploaded</span>
                            : <span className="text-gray-400">— not uploaded</span>}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-gray-400 mt-2">Re-upload below to replace documents.</p>
                  </div>
                )}
                {!form.loanType ? (
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 py-10 text-center">
                    <svg className="w-10 h-10 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m0 0v2m0-2h2m-2 0H10m2-6a4 4 0 100-8 4 4 0 000 8zm-9 9a9 9 0 1118 0H3z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-400">Select a loan type to upload documents</p>
                  </div>
                ) : reqList.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No required documents defined for this loan type.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {reqList.map(docName => (
                      <DocumentCapture key={docName} field={docName} label={docName}
                        files={reqDocs[docName] || []} error={documentErrs[docName]}
                        onChange={onDocChange} onClear={clearDoc} onCamera={setCameraField}
                        onError={msg => showToast('error', msg)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-6 py-2 rounded-lg transition-colors">
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// ── View Details Modal ────────────────────────────────────────────────────────
function ViewDetailsModal({ open, onClose, record }) {
  if (!open || !record) return null;

  const formatDate = d =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const Row = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-4 py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:w-36 flex-shrink-0">{label}</span>
      <span className="text-sm text-gray-800">{value || '—'}</span>
    </div>
  );

  const statusLabel = val =>
    callStatusOptions.find(o => o.value === val)?.label || val || '—';

  const statusBadgeClass = val => {
    switch (val) {
      case 'warm_lead':      return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'call_back':      return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'not_interested': return 'bg-red-50 text-red-600 border-red-200';
      case 'cold_lead':      return 'bg-gray-100 text-gray-600 border-gray-300';
      case 'not_connected':  return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:               return '';
    }
  };

  const docs = record.reqDocuments || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-white/10 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.18),0_8px_32px_rgba(0,0,0,0.12)] ring-1 ring-black/5 w-full max-w-5xl my-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Caller Details</h2>
            {record.isDraft && (
              <span className="inline-block mt-0.5 text-[10px] font-semibold bg-orange-100 text-orange-600 px-2 py-0.5 rounded uppercase tracking-wide">
                Draft
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <Row label="Caller"       value={record.caller?.name || record.callerName} />
          <Row label="Case Name"    value={record.caseName} />
          <Row label="Mobile"       value={record.mobileNumber} />
          <Row label="Referred By"  value={record.referredBy} />
          <Row label="Loan Type"    value={record.loanType} />
          <Row label="Amount"       value={record.amountRequired != null ? `₹${Number(record.amountRequired).toLocaleString('en-IN')}` : null} />
          <Row label="Remarks"      value={record.remarks} />

          {/* Call Status */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-4 py-2.5 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:w-36 flex-shrink-0">Status</span>
            {record.callStatus ? (
              <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${statusBadgeClass(record.callStatus)}`}>
                {statusLabel(record.callStatus)}
              </span>
            ) : <span className="text-sm text-gray-400">—</span>}
          </div>

          {record.callBackDate && (
            <Row label="Call Back Date" value={formatDate(record.callBackDate)} />
          )}

          {/* Disposition */}
          {record.dispositionChecked && (
            <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-4 py-2.5 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:w-36 flex-shrink-0">Disposition</span>
              <span className="text-sm text-gray-800">{record.dispositionRemark || 'Yes'}</span>
            </div>
          )}

          <Row label="Date" value={formatDate(record.createdAt)} />

          {/* Documents */}
          {docs.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Documents</p>
              <div className="space-y-2">
                {docs.map((d, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-sm text-gray-700">{d.name}</span>
                    {d.fileUrl ? (
                      <a
                        href={d.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium underline underline-offset-2"
                      >
                        View
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400">Not uploaded</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CallerList() {
  const { userInfo } = useSelector(s => s.userLogin || {});
  const { getRoleData } = useSelector(s => s.getRoleInfo || {});
  const role = getRoleData || userInfo?.role || {};
  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  const [loanTypes, setLoanTypes] = useState([]);
  const [records, setRecords] = useState([]);
  const [assignedCallers, setAssignedCallers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [toast, setToast] = useState(null);

  // Filters
  const [filterCaller, setFilterCaller]       = useState([]);
  const [filterLoanType, setFilterLoanType]   = useState([]);
  const [filterStatus, setFilterStatus]       = useState([]);
  const [filterDocStatus, setFilterDocStatus] = useState([]);
  const [filterDateFrom, setFilterDateFrom]   = useState('');
  const [filterDateTo, setFilterDateTo]       = useState('');
  const [searchName, setSearchName]           = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];

  const showToast = (type, msg) => setToast({ type, msg });

  useEffect(() => {
    axios.get(`${API}/loan-types`, authConfig)
      .then(({ data }) => setLoanTypes(data))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAssignedCallers = () => {
    const myId = (userInfo?.id || userInfo?._id)?.toString();
    const isSuperAdmin = userInfo?.role?.name === 'Super Admin';
    axios.get(`${API}/users`, authConfig)
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : [];

        if (isSuperAdmin) {
          setAssignedCallers(all.map(u => ({ id: u._id?.toString(), name: u.name })));
          return;
        }

        // Build parent → children map for BFS traversal
        const childrenOf = {};
        all.forEach(u => {
          const parentId = (u.assignedTo?._id || u.assignedTo)?.toString();
          if (parentId) {
            if (!childrenOf[parentId]) childrenOf[parentId] = [];
            childrenOf[parentId].push({ id: u._id?.toString(), name: u.name });
          }
        });

        // BFS from the current user to collect all subordinates at any depth
        const result = [];
        const visited = new Set();
        const queue = [myId];
        while (queue.length > 0) {
          const cur = queue.shift();
          for (const child of (childrenOf[cur] || [])) {
            if (!visited.has(child.id)) {
              visited.add(child.id);
              result.push(child);
              queue.push(child.id);
            }
          }
        }

        if (result.length > 0) {
          setAssignedCallers(result);
        } else {
          // Regular caller: show only self
          const self = all.find(u => u._id?.toString() === myId);
          setAssignedCallers(self ? [{ id: self._id?.toString(), name: self.name }] : []);
        }
      })
      .catch(() => {});
  };

  const fetchRecords = () => {
    setLoading(true);
    // Search/filter/pagination below all run client-side, so fetch the full
    // dataset in one call instead of relying on the backend's default limit=50.
    axios.get(`${API}/callers?limit=1000000`, authConfig)
      .then(({ data }) => setRecords(Array.isArray(data) ? data : data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const refresh = () => {
    loadAssignedCallers();
    fetchRecords();
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const handleDelete = async id => {
    if (!window.confirm('Delete this caller enquiry?')) return;
    try {
      await axios.delete(`${API}/callers/${id}`, authConfig);
      showToast('success', 'Deleted.');
      fetchRecords();
    } catch (err) {
      showToast('error', err?.response?.data?.message || 'Failed to delete.');
    }
  };

  const formatDate = d => d
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const getDocStatus = row => {
    const docs = row.reqDocuments || [];
    if (docs.length === 0) return 'Pending';
    const uploaded = docs.filter(d => d.fileUrl).length;
    if (uploaded === 0) return 'Pending';
    if (uploaded === docs.length) return 'Completed';
    return 'Partially';
  };

  const docStatusBadge = status => {
    if (status === 'Completed') return 'bg-green-50 text-green-700 border-green-100';
    if (status === 'Partially') return 'bg-yellow-50 text-yellow-700 border-yellow-100';
    return 'bg-red-50 text-red-600 border-red-100';
  };

  // Callers from assigned users (same logic as DailyCallForm)
  const callerOptions = assignedCallers;

  const callerFilterActive = filterCaller.length > 0 && filterCaller.length < callerOptions.length;
  const hasFilters = callerFilterActive || filterLoanType.length || filterStatus.length || filterDocStatus.length || filterDateFrom || filterDateTo || searchName.trim();

  const filteredRecords = records.filter(row => {
    if (callerFilterActive) {
      const id = row.caller?._id || row.caller || '';
      if (!filterCaller.includes(id)) return false;
    }
    if (filterLoanType.length && !filterLoanType.includes(row.loanType)) return false;
    if (filterStatus.length && !filterStatus.includes(row.callStatus)) return false;
    if (filterDocStatus.length && !filterDocStatus.includes(getDocStatus(row))) return false;
    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      from.setHours(0, 0, 0, 0);
      if (new Date(row.createdAt) < from) return false;
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(row.createdAt) > to) return false;
    }
    if (searchName.trim()) {
      const q = searchName.trim().toLowerCase();
      const matchesName = (row.caseName || '').toLowerCase().includes(q);
      const matchesMobile = (row.mobileNumber || '').toLowerCase().includes(q);
      if (!matchesName && !matchesMobile) return false;
    }
    return true;
  });

  const resetFilters = () => {
    setFilterCaller([]); setFilterLoanType([]); setFilterStatus([]);
    setFilterDocStatus([]); setFilterDateFrom(''); setFilterDateTo(''); setSearchName('');
  };

  useEffect(() => { setCurrentPage(1); }, [filterCaller, filterLoanType, filterStatus, filterDocStatus, filterDateFrom, filterDateTo, searchName, records.length, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
  const pageSafe = Math.min(currentPage, totalPages);
  const paginatedRecords = filteredRecords.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  const selectCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white';

  const exportToCSV = () => {
    const headers = ['S.No', 'Caller', 'Case Name', 'Mobile', 'Loan Type', 'Status', 'Call Back Date', 'Doc Status', 'Date'];
    const rows = filteredRecords.map((row, idx) => [
      idx + 1,
      row.caller?.name || row.callerName || '',
      row.caseName || '',
      row.mobileNumber || '',
      row.loanType || '',
      callStatusLabel(row.callStatus),
      row.callBackDate ? formatDate(row.callBackDate) : '',
      getDocStatus(row),
      formatDate(row.createdAt),
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `caller-list-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-2 py-3 sm:p-6 bg-gray-50 min-h-screen">
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-600'}`}>
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Caller List</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="inline-flex items-center gap-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 text-sm font-medium px-4 py-2 rounded-lg transition-colors shadow-sm"
          >
            <RotateCcw size={15} />
            Refresh
          </button>
          <button
            onClick={exportToCSV}
            disabled={filteredRecords.length === 0}
            className="inline-flex items-center gap-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium px-4 py-2 rounded-lg transition-colors shadow-sm"
          >
            <Download size={15} />
            Export
          </button>
          {!!role?.callerListAdd && (
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors shadow-sm"
            >
              <Plus size={16} />
              Add Enquiry
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="mb-3">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Search by Name / Mobile</label>
          <div className="relative max-w-sm">
            <input
              type="text"
              value={searchName}
              onChange={e => setSearchName(e.target.value)}
              placeholder="Search by case name or mobile…"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            {searchName && (
              <button
                type="button"
                onClick={() => setSearchName('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Caller</label>
            <MultiSelect
              options={callerOptions.map(c => ({ value: c.id, label: c.name }))}
              value={filterCaller}
              onChange={setFilterCaller}
              placeholder="All"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Loan Type</label>
            <MultiSelect
              options={loanTypes.map(lt => ({ value: lt.name, label: lt.name }))}
              value={filterLoanType}
              onChange={setFilterLoanType}
              placeholder="All"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
            <MultiSelect
              options={callStatusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="All"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Doc Status</label>
            <MultiSelect
              options={[
                { value: 'Completed', label: 'Completed' },
                { value: 'Partially', label: 'Partially' },
                { value: 'Pending',   label: 'Pending'   },
              ]}
              value={filterDocStatus}
              onChange={setFilterDocStatus}
              placeholder="All"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">From Date</label>
            <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className={selectCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">To Date</label>
            <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className={selectCls} />
          </div>
        </div>
        {hasFilters && (
          <div className="mt-3 flex items-center gap-3">
            <span className="text-xs text-gray-500">{filteredRecords.length} of {records.length} records</span>
            <button onClick={resetFilters} className="text-xs text-blue-600 hover:text-blue-800 font-medium underline underline-offset-2">
              Clear filters
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredRecords.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-12">{hasFilters ? 'No records match the selected filters.' : 'No caller enquiries yet.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr>
                  {['S.No', 'Caller', 'Case Name', 'Mobile', 'Loan Type', 'Status', 'Call Back Date', 'Doc Status', 'Date', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedRecords.map((row, idx) => (
                  <tr key={row._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-500">{(pageSafe - 1) * pageSize + idx + 1}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.caller?.name || row.callerName || '—'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">
                      <div className="flex items-center gap-2">
                        {row.caseName ? (
                          <Link
                            to={`/admin/caller-list/${row._id}`}
                            className="hover:text-blue-600 hover:underline underline-offset-2 transition-colors"
                          >
                            {row.caseName}
                          </Link>
                        ) : '—'}
                        {row.isDraft && (
                          <span className="inline-block bg-orange-50 text-orange-600 text-xs font-medium px-1.5 py-0.5 rounded border border-orange-200">
                            Draft
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.mobileNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.loanType || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      {row.callStatus ? (
                        <span className="inline-block bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full border border-blue-100">
                          {callStatusLabel(row.callStatus)}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {row.callBackDate ? formatDate(row.callBackDate) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(() => {
                        const s = getDocStatus(row);
                        return (
                          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${docStatusBadge(s)}`}>
                            {s}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(row.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewRecord(row)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                          title="View details"
                        >
                          <Eye size={15} strokeWidth={2} />
                        </button>
                        {!!role?.callerListEdit && row.isDraft && (
                          <button
                            onClick={() => setEditRecord(row)}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-orange-300 bg-orange-50 text-orange-600 hover:bg-orange-100 text-xs font-medium transition-colors"
                            title="Continue draft"
                          >
                            <Pencil size={12} strokeWidth={2.2} />
                            Draft
                          </button>
                        )}
                        {!!role?.callerListEdit && !row.isDraft && (
                          <button
                            onClick={() => setEditRecord(row)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                            title="Edit"
                          >
                            <Pencil size={15} strokeWidth={2.2} />
                          </button>
                        )}
                        {!!role?.callerListDelete && (
                          <button
                            onClick={() => handleDelete(row._id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={15} strokeWidth={2.2} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filteredRecords.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-xs text-gray-500">
                Showing {(pageSafe - 1) * pageSize + 1}–{Math.min(pageSafe * pageSize, filteredRecords.length)} of {filteredRecords.length}
              </span>
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                Rows per page
                <select
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {pageSizeOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={pageSafe <= 1}
                className="inline-flex items-center justify-center h-8 px-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs"
              >
                First
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={pageSafe <= 1}
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={15} />
              </button>
              <span className="text-xs text-gray-600 px-2">Page {pageSafe} of {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={pageSafe >= totalPages}
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={15} />
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={pageSafe >= totalPages}
                className="inline-flex items-center justify-center h-8 px-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      <AddCallerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchRecords}
        userInfo={userInfo}
        loanTypes={loanTypes}
        showToast={showToast}
      />
      <EditCallerModal
        open={!!editRecord}
        onClose={() => setEditRecord(null)}
        onSaved={fetchRecords}
        userInfo={userInfo}
        loanTypes={loanTypes}
        showToast={showToast}
        initial={editRecord}
      />
      <ViewDetailsModal
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        record={viewRecord}
      />
    </div>
  );
}
